import { Field, Form, Formik } from 'formik';
import { HTTPError } from 'ky';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { FormComponentBaseProps } from '@tmh-website/components/blocks/Form';
import { cmsWebClient } from '@tmh-website/services/cms.client.service';
import { StaticFormPayload } from '@tmh-website/services/form.service';
import Yup from '@tmh-website/services/validation.service';
import { Breakpoint, LocalePrefix } from '@tmh-website/shared/const';
import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import Button from '@tmh-website/ui/Button';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import { ErrorMessage, SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import Select from '@tmh-website/ui/Select';
import Textarea from '@tmh-website/ui/Textarea';

import * as StyledStaticForms from './StaticForms.styles';

export const ComplaintFormValidation = Yup.object({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  company: Yup.string(),
  email: Yup.string().required(),
  phone: Yup.string().required(),
  message: Yup.string().required(),
  industry: Yup.string().required(),
  zipCode: Yup.string().required(),
  street: Yup.string().required(),
  city: Yup.string().required(),
  orderNumber: Yup.string().required(),
  gRecaptchaResponse: Yup.string().required(),
}).strict();

export type Values = Yup.InferType<typeof ComplaintFormValidation>;

export const ComplaintFormLabel: Record<keyof Values, string> = {
  firstName: 'global:label.firstName',
  lastName: 'global:label.lastName',
  company: 'global:label.company',
  email: 'global:label.email',
  phone: 'global:label.phone',
  message: 'global:label.yourMessage',
  industry: 'blocks:staticForms.customerTypeLabel',
  zipCode: 'global:label.zip',
  street: 'global:label.street',
  city: 'global:label.city',
  orderNumber: 'blocks:staticForms.orderNumber',
  gRecaptchaResponse: '',
};

const ComplaintForm = ({
  formParameters,
  formElementThemeColor,
  buttonThemeColor,
  buttonHoverThemeColor,
  locale,
}: FormComponentBaseProps) => {
  const { t } = useTranslation();
  const {
    mutateAsync: submitMutation,
    isSuccess,
    isError,
  } = useMutation<void, HTTPError, Values>(values => {
    const payload: StaticFormPayload = {
      formParameters,
      locale: locale,
      pagePath: window.location.pathname,
      gRecaptchaResponse: values.gRecaptchaResponse,
      fields: Object.keys(values)
        .filter(key => key !== 'gRecaptchaResponse')
        .map(name => ({ name, label: t(ComplaintFormLabel[name]), value: values[name] })),
    };
    return cmsWebClient.post('static-form/submit', { json: payload }).json();
  });

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        zipCode: '',
        street: '',
        city: '',
        industry: '',
        orderNumber: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        gRecaptchaResponse: '',
      }}
      validationSchema={ComplaintFormValidation}
      onSubmit={values => submitMutation(values)}>
      {({ isSubmitting, setFieldValue, touched, errors }) => (
        <Form data-cy='complaintForm'>
          <>
            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.firstName)}
                name='firstName'
                autoComplete='given-name'
                required
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.lastName)}
                name='lastName'
                autoComplete='family-name'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.zipCode)}
                name='zipCode'
                autoComplete='zip-code'
                required
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.street)}
                name='street'
                autoComplete='street'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.city)}
                name='city'
                autoComplete='city'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={2}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.company)}
                name='company'
                autoComplete='organization'
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.phone)}
                name='phone'
                autoComplete='phone'
                type='tel'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.email)}
                name='email'
                autoComplete='email'
                type='email'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Select}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.industry)}
                name='industry'
                autoComplete='industry'
                options={t('blocks:staticForms.customerTypeOptions', { returnObjects: true })}
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.orderNumber)}
                name='orderNumber'
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Textarea}
                themeColor={formElementThemeColor}
                label={t(ComplaintFormLabel.message)}
                name='message'
                rows='4'
                required
              />
            </FormSection>
            <StyledStaticForms.CaptchaContainer>
              <ReCAPTCHA
                hl={getCaptchaHlBasedOnLocale(locale)}
                onChange={token => setFieldValue('gRecaptchaResponse', token)}
                sitekey={
                  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? process.env.NX_NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
                }
                size='normal'
              />
              {touched.gRecaptchaResponse && errors.gRecaptchaResponse && (
                <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
              )}
            </StyledStaticForms.CaptchaContainer>
            {!isSuccess && (
              <Button
                type='submit'
                themeColor={buttonThemeColor}
                hoverThemeColor={buttonHoverThemeColor}
                isLoading={isSubmitting}
                disabled={isSuccess || Object.keys(touched).length === 0 || Object.keys(errors).length > 0}
                minWidth>
                {t('global:send')}
              </Button>
            )}
            {isSuccess && <SuccessMessage marginTop>{t('global:contact.thanksMessage')}</SuccessMessage>}
            {isError && <ErrorMessage marginTop>{t('global:errorUnknown')}</ErrorMessage>}
          </>
        </Form>
      )}
    </Formik>
  );
};
export default ComplaintForm;
